import React, { useState } from 'react';

const EarnPage = ({ profileData, navigate }) => {
  const stakedPositions = profileData.stakedPositions || [];
  const hasStakes = stakedPositions.length > 0;
  const userBalances = profileData.balances || {};
  
  const [expandedCategories, setExpandedCategories] = useState({ 0: true }); // Gold category expanded by default

  const toggleCategory = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  // Rough token prices for demo (in real app, these would come from an API)
  const tokenPrices = {
    'GLDT': 2100, // Approximate gold price per token
    'ckUSDT': 1, // Stablecoin
    'USDC': 1, // Stablecoin
    'ICP': 12, // Approximate ICP price
    'nICP': 12 // Similar to ICP
  };

  const calculateMissedEarnings = (balance, apy, tokenKey) => {
    if (!balance || balance === 0) return 0;
    const apyDecimal = parseFloat(apy.replace('%', '')) / 100;
    const tokenPrice = tokenPrices[tokenKey] || 1;
    const usdValue = balance * tokenPrice;
    return usdValue * apyDecimal;
  };

  const formatCurrency = (amount) => {
    if (amount < 0.01) return '$0.00';
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
    return `$${amount.toFixed(2)}`;
  };

  const earningCategories = [
    {
      title: 'Gold',
      description: 'Stake GLDT tokens to earn rewards backed by physical gold',
      icon: '🥇',
      products: [
        {
          name: 'GLDT',
          description: 'Gold-backed liquid tokens',
          apy: '8.5%',
          risk: 'Medium',
          balance: userBalances.GLDT || 0,
          balanceKey: 'GLDT',
          onClick: () => {
            console.log('Navigate to GLDT staking');
          }
        }
      ]
    },
    {
      title: 'Stablecoins',
      description: 'Earn yield on your stable assets with low risk',
      icon: '💰',
      products: [
        {
          name: 'USDT',
          description: 'Tether stablecoin',
          apy: '4.2%',
          risk: 'Low',
          balance: userBalances.ckUSDT || 0,
          balanceKey: 'ckUSDT',
          onClick: () => {
            console.log('Navigate to USDT staking');
          }
        },
        {
          name: 'USDC',
          description: 'USD Coin stablecoin',
          apy: '4.5%',
          risk: 'Low',
          balance: 0, // User doesn't have USDC in the sample data
          balanceKey: 'USDC',
          onClick: () => {
            console.log('Navigate to USDC staking');
          }
        }
      ]
    },
    {
      title: 'ICP',
      description: 'Internet Computer Protocol staking options',
      icon: '∞',
      products: [
        {
          name: 'Native ICP Staking',
          description: 'Traditional neuron staking',
          apy: '10.0%',
          risk: 'Low',
          balance: userBalances.ICP || 0,
          balanceKey: 'ICP',
          onClick: () => {
            console.log('Navigate to native ICP staking');
          }
        },
        {
          name: 'ICP Liquid Staking',
          description: 'Flexible liquid staking',
          apy: '15.0%',
          risk: 'Medium',
          balance: userBalances.ICP || 0,
          balanceKey: 'ICP',
          onClick: () => {
            navigate('/liquid-staking');
          }
        }
      ]
    }
  ];

  return (
    <div>
      <div className="earn-header" style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '12px' }}>
          Earn & Rewards
        </h1>
        <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.5' }}>
          Grow your crypto portfolio by staking your tokens and earning rewards. 
          Choose from various earning strategies based on your risk tolerance.
        </p>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
          Earning Categories
        </h2>
        <div className="earn-categories-expandable">
          {earningCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="expandable-category">
              <div 
                className="category-summary"
                onClick={() => toggleCategory(categoryIndex)}
              >
                <div className="category-info">
                  <div className="category-header-row">
                    <div className="category-icon-title">
                      <span className="category-icon">{category.icon}</span>
                      <span className="category-title">{category.title}</span>
                    </div>
                    <div className="category-metrics">
                      <div className="market-apr">
                        <span className="metric-value">
                          {category.products.length > 1 
                            ? `${Math.min(...category.products.map(p => parseFloat(p.apy)))}% - ${Math.max(...category.products.map(p => parseFloat(p.apy)))}%`
                            : category.products[0]?.apy || '0%'
                          }
                        </span>
                        <span className="metric-label">Market APR</span>
                      </div>
                      <div className="missed-earnings">
                        <span className="metric-value">
                          {formatCurrency(
                            category.products.reduce((total, product) => {
                              return total + calculateMissedEarnings(product.balance, product.apy, product.balanceKey);
                            }, 0)
                          )}
                        </span>
                        <span className="metric-label">Missing out yearly</span>
                      </div>
                      <div className="expand-icon">
                        {expandedCategories[categoryIndex] ? '▲' : '▼'}
                      </div>
                    </div>
                  </div>
                  <div className="category-description">{category.description}</div>
                </div>
              </div>

              {expandedCategories[categoryIndex] && (
                <div className="category-products-expanded">
                  {category.products.map((product, productIndex) => (
                    <div 
                      key={productIndex} 
                      className="product-row"
                      onClick={product.onClick}
                    >
                      <div className="product-info">
                        <div className="product-name">{product.name}</div>
                        <div className="product-description">{product.description}</div>
                      </div>
                      <div className="product-metrics">
                        <div className="product-apy">
                          <span className="metric-value">{product.apy}</span>
                          <span className="metric-label">APY</span>
                        </div>
                        <div className="product-balance">
                          <span className="metric-value">{product.balance.toFixed(2)}</span>
                          <span className="metric-label">Your Balance</span>
                        </div>
                        <div className="product-missed-earnings">
                          <span className="metric-value">
                            {formatCurrency(calculateMissedEarnings(product.balance, product.apy, product.balanceKey))}
                          </span>
                          <span className="metric-label">Missing yearly</span>
                        </div>
                        <div className="product-term">
                          <span className="metric-value">Flexible</span>
                          <span className="metric-label">Term</span>
                        </div>
                        <button className="subscribe-btn">Check this out</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="staked-positions">
        <div className="section-header">
          <h2 className="section-title">Staked Positions</h2>
        </div>

        {!hasStakes ? (
          <div className="empty-state">
            <div className="empty-icon">⭐</div>
            <div className="empty-title">No staked positions yet</div>
            <div className="empty-description">
              Start earning rewards by staking your tokens in one of the earning categories above.
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Protocol</th>
                <th>Asset</th>
                <th>Amount</th>
                <th>APY</th>
                <th>Duration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stakedPositions.map((position, index) => (
                <tr key={index}>
                  <td>
                    <div style={{ fontWeight: '500' }}>{position.protocol}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ 
                        fontSize: '16px',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {position.asset === 'GLDT' ? '🥇' : '∞'}
                      </span>
                      {position.asset}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '500' }}>{position.amount}</div>
                  </td>
                  <td>
                    <div style={{ color: '#34c759', fontWeight: '500' }}>{position.apy}</div>
                  </td>
                  <td>{position.duration}</td>
                  <td>
                    <span style={{
                      background: '#34c759',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      Active
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EarnPage;
